import React, { useState } from 'react';
import {
  CheckCircle,
  XCircle,
  ShieldAlert,
  Info,
  User,
  CalendarClock,
  RefreshCw,
} from 'lucide-react';
import { AgentProps } from './Interfaces/AgentProps';

type Props = {
  agent?: AgentProps;
};

const AgentTestComponent : React.FC<Props> = ({ agent }) => {
  const [testResults, setTestResults] = useState<any[]>([
    { step: 'Input Data Validation', status: 'Pending', error: '' },
    { step: 'Main Agent Logic', status: 'Pending', error: '' },
    { step: 'Logging', status: 'Pending', error: '' },
    { step: 'Secure Resource Access', status: 'Pending', error: '' },
  ]);

  const [finalStatus, setFinalStatus] = useState<string>('Pending');
  const [lastTested, setLastTested] = useState<string>('Not yet tested');

  const runAllTests = () => {
    const updatedResults = testResults.map((test) => {
      const passed = Math.random() > 0.3;
      return {
        ...test,
        status: passed ? 'Pass' : 'Fail',
        error: passed ? '' : `Issue in ${test.step}: simulated error.`,
      };
    });

    setTestResults(updatedResults);
    setFinalStatus(updatedResults.every((t) => t.status === 'Pass') ? 'All Tests Passed' : 'Some Tests Failed');
    setLastTested(new Date().toLocaleString());
  };

  const statusIcon = (status: string) => {
    if (status === 'Pass') return <CheckCircle className="w-4 h-4 text-green-400" />;
    if (status === 'Fail') return <XCircle className="w-4 h-4 text-red-400" />;
    return <RefreshCw className="w-4 h-4 text-yellow-400 animate-spin" />;
  };

  return (
    <div className="p-6 max-w-5xl mx-auto text-white space-y-6">
      {/* Header */}
      <div className="space-y-1">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <ShieldAlert className="w-5 h-5 text-indigo-500" />
          Agent Test & Validation
        </div>
        <p className="text-xs text-gray-400">
          Validate critical execution paths before deployment.
        </p>
      </div>

      {/* Metadata */}
      <div className="grid sm:grid-cols-3 gap-3 text-xs text-gray-400">
        <div className="flex items-center gap-2">
          <Info className="w-3 h-3" />
          Version: <span className="text-white font-medium ml-1">v2.3.1</span>
        </div>
        <div className="flex items-center gap-2">
          <User className="w-3 h-3" />
          User: <span className="text-white font-medium ml-1">admin@company.com</span>
        </div>
        <div className="flex items-center gap-2">
          <CalendarClock className="w-3 h-3" />
          Last Tested: <span className="text-white font-medium ml-1">{lastTested}</span>
        </div>
      </div>

      {/* Run Button */}
      <div>
        <button
          onClick={runAllTests}
          className="bg-indigo-600 hover:bg-indigo-700 text-xs px-4 py-2 rounded-md text-white transition"
        >
          Run All Tests
        </button>
      </div>

      {/* Tests */}
      <div className="space-y-3">
        {testResults.map((test, idx) => (
          <div key={idx} className="bg-neutral-900 border border-neutral-800 rounded-md p-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2 text-sm">
                {statusIcon(test.status)}
                <span>{test.step}</span>
              </div>
              <div className={`text-xs font-medium ${test.status === 'Pass' ? 'text-green-400' : test.status === 'Fail' ? 'text-red-400' : 'text-yellow-400'}`}>
                {test.status}
              </div>
            </div>
            {test.error && (
              <div className="text-xs text-red-300 mt-2 pl-6">{test.error}</div>
            )}
          </div>
        ))}
      </div>

      {/* Final Status */}
      {finalStatus !== 'Pending' && (
        <div className="text-sm text-center pt-4 flex justify-center items-center gap-2">
          {finalStatus === 'All Tests Passed' ? (
            <CheckCircle className="w-4 h-4 text-green-400" />
          ) : (
            <XCircle className="w-4 h-4 text-red-400" />
          )}
          <span
            className={`font-semibold ${
              finalStatus === 'All Tests Passed' ? 'text-green-400' : 'text-red-400'
            }`}
          >
            {finalStatus}
          </span>
        </div>
      )}
    </div>
  );
};

export default AgentTestComponent;
